import { useQuery } from "react-query";
import {
  Box, Heading, Text, VStack, Spinner, useColorModeValue, Button, Flex, Divider, Badge,
  useDisclosure, Modal, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay,
  SimpleGrid, HStack, Stack, Tag, useToast, Image, Wrap, WrapItem, Link, Select,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { getMySubmittedQuestionPapers } from "API/attemptReviewApi";


/* ---------- Helpers: MCQM normalization ---------- */


// Normalize MCQM submitted answers (supports both singular/plural keys and coerces to numbers)
const getMcqmSubmittedArray = (attempt: any): number[] => {
  const arr =
    attempt?.mcqmSubmittedAnswer != null
      ? attempt.mcqmSubmittedAnswer
      : attempt?.mcqmSubmittedAnswers != null
      ? attempt.mcqmSubmittedAnswers
      : [];
  if (!Array.isArray(arr)) return [];
  return arr.map((v) => Number(v)).filter((n) => Number.isFinite(n));
};


/* ---------- Helpers: formatting ---------- */


const formatAnswer = (answer: any) => {
  if (answer == null || answer === "") return "—";
  if (Array.isArray(answer) && answer.length === 0) return "—";
  if (Array.isArray(answer)) return answer.join(", ");
  if (typeof answer === "boolean") return answer ? "True" : "False";
  return String(answer);
};


const formatUserAnswer = (question: any, attempt: any) => {
  const type = (question?.type || "").toLowerCase();
  switch (type) {
    case "mcqs":
      return question?.mcqSOptions?.[attempt?.mcqsSubmittedAnswer] ?? "—";
    case "mcqm": {
      const submitted = getMcqmSubmittedArray(attempt);
      if (!submitted.length) return "—";
      return submitted
        .map((idx: number) => question?.mcqMOptions?.[idx] ?? String(idx))
        .filter(Boolean)
        .join(", ");
    }
    case "tf":
      if (typeof attempt?.tfSubmittedAnswer === "boolean")
        return attempt.tfSubmittedAnswer ? "True" : "False";
      return "—";
    case "short":
      return attempt?.shortSubmittedAnswer ?? "—";
    case "long":
    case "la":
      return attempt?.longSubmittedAnswer ?? "—";
    case "diagram":
      return attempt?.diagramSubmittedAnswer ?? "—";
    default:
      return attempt?.answer ?? "—";
  }
};


const formatCorrectAnswer = (question: any) => {
  const type = (question?.type || "").toLowerCase();
  switch (type) {
    case "mcqs":
      if (question?.mcqSOptions && typeof question.mcqSAnswer === "number") {
        return question.mcqSOptions[question.mcqSAnswer] ?? "—";
      }
      return "—";
    case "mcqm": {
      const arr = Array.isArray(question?.mcqMAnswer) ? question.mcqMAnswer : [];
      if (!arr.length) return "—";
      return arr
        .map((i: number) => question?.mcqMOptions?.[Number(i)] ?? String(i))
        .filter(Boolean)
        .join(", ");
    }
    case "tf":
      if (typeof question.tFAnswer === "boolean") {
        return question.tFAnswer ? "True" : "False";
      }
      return "—";
    default:
      return "Manual Review";
  }
};


/* ---------- Correctness logic ---------- */


const arraysEqual = (a: any[], b: any[]) => {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  const A = [...a].map(Number).sort((x, y) => x - y);
  const B = [...b].map(Number).sort((x, y) => x - y);
  return A.every((v, i) => v === B[i]);
};


const isCheckableType = (type?: string) => {
  if (!type) return false;
  const t = String(type).toLowerCase();
  return t === "mcqs" || t === "mcqm" || t === "tf";
}


/**
 * Determine final correctness:
 * - Not attempted => false
 * - If admin override (attempt.isCorrect boolean) exists => use it
 * - Objective (MCQS/MCQM/TF): auto-check if no admin override
 * - Subjective/Diagram without admin => null (pending)
 */
const getFinalCorrectness = (question: any, attempt: any): boolean | null => {
  if (!question || !attempt) return null;
  const type = (question?.type || "").toLowerCase();


  if (!attempt.attempted) return false;


  // Admin override has priority
  if (typeof attempt.isCorrect === "boolean") return attempt.isCorrect;


  if (type === "mcqs") {
    return attempt.mcqsSubmittedAnswer === question.mcqSAnswer;
  }


  if (type === "mcqm") {
    const submitted = getMcqmSubmittedArray(attempt);
    const correct = Array.isArray(question?.mcqMAnswer)
      ? question.mcqMAnswer.map((v: any) => Number(v)).filter((n: number) => Number.isFinite(n))
      : [];
    return arraysEqual(submitted, correct);
  }


  if (type === "tf") {
    return attempt.tfSubmittedAnswer === question.tFAnswer;
  }


  if (["short", "long", "diagram"].includes(type)) {
    // subjective without admin review is pending
    return null;
  }


  return null;
};


/* ---------- Section/Marks mapping ---------- */


const getSnapshotQuestionId = (q: any): string | null => {
  if (!q) return null;
  const cand = q._id || q.id || q.questionId;
  return cand ? String(cand) : null;
};


const buildSectionMarksMap = (paper: any): Map<string, number> => {
  const map = new Map<string, number>();
  const sections = paper?.sections;
  if (!Array.isArray(sections)) return map;


  for (let si = 0; si < sections.length; si++) {
    const qs = sections[si]?.questions;
    if (!Array.isArray(qs)) continue;
    for (let qi = 0; qi < qs.length; qi++) {
      const it = qs[qi];
      const qid = it?.questionId ? String(it.questionId) : null;
      if (qid) {
        const m = typeof it?.marks === "number" ? it.marks : 0;
        map.set(qid, m);
      }
    }
  }
  return map;
};


const sumMapValues = (m: Map<string, number>): number =>
  Array.from(m.values()).reduce((acc, v) => acc + (typeof v === "number" ? v : 0), 0);


const getPossibleMarksFromSections = (item: any, sectionMap: Map<string, number>): number => {
  const qid = getSnapshotQuestionId(item?.question);
  if (!qid) return 1;
  const m = sectionMap.get(qid);
  return typeof m === "number" ? m : 1;
};


/**
 * Awarded from possible-based logic with admin override & auto-check:
 * - Not attempted => 0
 * - finalCorrectness true => full possible; else 0
 */
const computeAwardedFromSections = (item: any, sectionMap: Map<string, number>): number => {
  const possible = getPossibleMarksFromSections(item, sectionMap);
  const final = getFinalCorrectness(item?.question, item);
  return final === true ? possible : 0;
};


/**
 * Totals from possible marks; awarded capped by paper totalMarks.
 * Uses ALL questions (objective + subjective).
 */
const computeTotalsFromSections = (attempt: any) => {
  const sectionMap = buildSectionMarksMap(attempt?.questionPaper);
  let paperTotal = sumMapValues(sectionMap);
  if (paperTotal === 0) {
    paperTotal =
      typeof attempt?.questionPaper?.totalMarks === "number"
        ? attempt.questionPaper.totalMarks
        : (attempt?.questionResult?.length || 0);
  }


  let awarded = 0;
  const qr = attempt?.questionResult || [];
  for (let i = 0; i < qr.length; i++) {
    awarded += computeAwardedFromSections(qr[i], sectionMap);
  }


  const declaredTotal =
    typeof attempt?.questionPaper?.totalMarks === "number"
      ? attempt.questionPaper.totalMarks
      : paperTotal;


  awarded = Math.min(awarded, declaredTotal);
  paperTotal = declaredTotal;


  return { paperTotal, awarded, sectionMap };
};


/* ---------- Image helpers ---------- */


const getBackendBaseUrl = () => {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin.replace(/:3000$/, ":9000").replace(/\/+$/, "");
  }
  return "";
};


const buildImageUrl = (img?: string): string | null => {
  if (!img || typeof img !== "string") return null;
  if (/^https?:\/\//i.test(img)) return img;
  const base = getBackendBaseUrl();
  return `${base}/upload/${encodeURIComponent(img)}`;
};


// Render a list of submitted uploads (images or PDFs)
const SubmittedUploads = ({ uploads }: { uploads: string[] }) => {
  if (!Array.isArray(uploads) || uploads.length === 0) return null;
  const base = getBackendBaseUrl();
  const toSrc = (u: string) => (/^https?:\/\//i.test(u) ? u : `${base}${u.startsWith("/") ? "" : "/"}${u}`);


  return (
    <Box mt={2}>
      <Text fontWeight="semibold" fontSize="sm" mb={1}>Submitted Files</Text>
      <Wrap spacing={3}>
        {uploads.map((u, i) => {
          const src = toSrc(u);
          const lower = u.toLowerCase();
          const isPdf = lower.endsWith(".pdf");
          return (
            <WrapItem key={`${u}-${i}`}>
              {isPdf ? (
                <Link href={src} isExternal color="blue.600" fontSize="sm" textDecoration="underline">
                  View PDF
                </Link>
              ) : (
                <Image
                  src={src}
                  alt="Submitted"
                  maxH="160px"
                  objectFit="contain"
                  borderWidth="1px"
                  borderRadius="md"
                  bg={useColorModeValue("gray.50", "gray.800")}
                />
              )}
            </WrapItem>
          );
        })}
      </Wrap>
    </Box>
  );
};


const DiagramInline = ({ question }: { question: any }) => {
  const bg = useColorModeValue("gray.50", "gray.900");
  const src = buildImageUrl(question?.questionImg);
  if (!src) return null;
  return (
    <Box mt={2} mb={2} borderWidth="1px" borderRadius="md" p={2} bg={bg} display="flex" justifyContent="flex-start">
      <Box as="img" src={src} alt="Diagram" maxH="280px" style={{ objectFit: "contain" }} />
    </Box>
  );
};


/* ---------- Option highlighting block ---------- */


const OptionsBlock = ({ item }: { item: any }) => {
  const q = item?.question || {};
  const type = (q?.type || "").toLowerCase();


  if (type === "tf") {
    const options: string[] = ["True", "False"];
    const correct =
      typeof q?.tFAnswer === "boolean" ? (q.tFAnswer ? "True" : "False") : null;
    const user =
      typeof item?.tfSubmittedAnswer === "boolean"
        ? item.tfSubmittedAnswer
          ? "True"
          : "False"
        : null;

    return (
      <HStack mt={3} spacing={3} wrap="wrap">
        {options.map((opt: string) => {
          const isCorrect = correct === opt;
          const isUser = user === opt;
          return (
            <Tag
              key={opt}
              size="md"
              variant="subtle"
              colorScheme={isCorrect ? "green" : isUser ? "blue" : "gray"}
            >
              {opt}
            </Tag>
          );
        })}
      </HStack>
    );
  }


  if (type === "mcqs" || type === "mcqm") {
    const options: string[] =
      (type === "mcqs" ? q?.mcqSOptions : q?.mcqMOptions) || [];
    const optionImages: string[] =
      (type === "mcqs" ? q?.mcqSOptionImages : q?.mcqMOptionImages) || [];
    const correctIdx: number[] =
      type === "mcqs"
        ? typeof q?.mcqSAnswer === "number"
          ? [q.mcqSAnswer]
          : []
        : Array.isArray(q?.mcqMAnswer)
        ? q.mcqMAnswer.map((v: any) => Number(v))
        : [];
    const userIdx: number[] =
      type === "mcqs"
        ? typeof item?.mcqsSubmittedAnswer === "number"
          ? [item.mcqsSubmittedAnswer]
          : []
        : getMcqmSubmittedArray(item);

    return (
      <Stack mt={3} spacing={2}>
        {options.map((opt, idx) => {
          const isCorrect = correctIdx.includes(idx);
          const isUser = userIdx.includes(idx);
          const img = optionImages[idx];
          const imgUrl = buildImageUrl(img);
          return (
            <HStack key={idx} spacing={2} align="flex-start">
              <Tag
                size="sm"
                colorScheme={isCorrect ? "green" : isUser ? "blue" : "gray"}
                variant="subtle"
                minW="24px"
                justifyContent="center"
              >
                {String.fromCharCode(65 + idx)}
              </Tag>
              <Box>
                {opt && <Text mb={imgUrl ? 2 : 0}>{opt}</Text>}
                {imgUrl && (
                  <Image
                    src={imgUrl}
                    alt={`Option ${String.fromCharCode(65 + idx)} image`}
                    maxH="120px"
                    maxW="180px"
                    objectFit="contain"
                    borderRadius="md"
                    borderWidth="1px"
                    bg={useColorModeValue("gray.50", "gray.800")}
                  />
                )}
              </Box>
              {isCorrect && (
                <Badge ml={2} colorScheme="green" variant="subtle">
                  Correct
                </Badge>
              )}
              {isUser && !isCorrect && (
                <Badge ml={2} colorScheme="blue" variant="subtle">
                  Your choice
                </Badge>
              )}
            </HStack>
          );
        })}
      </Stack>
    );
  }


  return null;
};


/* ---------- StatBox ---------- */


const StatBox = ({ label, value, color }: { label: string; value: number | string; color?: string }) => (
  <Box minW="90px" textAlign="center">
    <Text fontSize="sm" fontWeight="semibold" mb={1}>{label}</Text>
    <Text fontSize="lg" fontWeight="bold" color={color ?? "inherit"}>{value}</Text>
  </Box>
);


/* ---------- Component ---------- */


export default function AttemptReview() {
  const toast = useToast();
  const [selectedAttempt, setSelectedAttempt] = useState<any>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // NEW: Filter state for question paper title
  const [selectedPaperTitle, setSelectedPaperTitle] = useState<string>("");

  const bg = useColorModeValue("white", "gray.700");
  const border = useColorModeValue("gray.200", "gray.600");


  const { data, isLoading, isError, error } = useQuery("myAttempts", getMySubmittedQuestionPapers, {
    onError: (err: any) => {
      toast({
        title: "Failed to load attempts",
        description: err && typeof err.message === "string" ? err.message : "Unknown error",
        status: "error",
        isClosable: true,
      });
    },
  });


  const sortedAttempts = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const arr = [...data];
    arr.sort((a: any, b: any) => {
      const at = new Date(a.updatedAt || a.createdAt || 0).getTime();
      const bt = new Date(b.updatedAt || b.createdAt || 0).getTime();
      if (bt !== at) return bt - at;
      return String(b._id).localeCompare(String(a._id));
    });
    return arr;
  }, [data]);

  // NEW: Extract unique question paper titles for dropdown
  const uniquePaperTitles = useMemo(() => {
    const titles = sortedAttempts.map(
      (att: any) => att.questionPaper?.title || "Untitled"
    );
    return Array.from(new Set(titles)).sort();
  }, [sortedAttempts]);

  // NEW: Filter attempts by selected paper title
  const filteredAttempts = useMemo(() => {
    if (!selectedPaperTitle) return sortedAttempts;
    return sortedAttempts.filter(
      (att: any) => (att.questionPaper?.title || "Untitled") === selectedPaperTitle
    );
  }, [sortedAttempts, selectedPaperTitle]);


  const openReview = (attempt: any) => {
    setSelectedAttempt(attempt);
    onOpen();
  };


  if (isLoading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="lg" />
      </Box>
    );
  }


  if (isError) {
    return (
      <Box textAlign="center" py={10}>
        <Text color="red.500">{(error as Error)?.message || "Error loading data"}</Text>
      </Box>
    );
  }


  if (!sortedAttempts || sortedAttempts.length === 0) {
    return (
      <Box textAlign="center" py={10}>
        <Text>No attempted question papers found</Text>
      </Box>
    );
  }


  return (
    <Box maxW="1200px" mx="auto" mt={4} p={4}>
      <Heading mb={5} textAlign="center" fontWeight="bold" letterSpacing="normal" fontSize="xl">
        Your Exam Attempts
      </Heading>

      {/* NEW: Question Paper Filter Dropdown */}
      <Flex mb={6} justify="space-between" align="center" wrap="wrap" gap={4}>
        <Box flex="1" />
        <HStack spacing={3}>
          <Text fontSize="sm" fontWeight="medium" color="gray.600">
            Filter by Question Paper:
          </Text>
          <Select
            placeholder="All Question Papers"
            value={selectedPaperTitle}
            onChange={(e) => setSelectedPaperTitle(e.target.value)}
            w="280px"
            size="sm"
            bg={bg}
            borderColor={border}
          >
            {uniquePaperTitles.map((title) => (
              <option key={title} value={title}>
                {title}
              </option>
            ))}
          </Select>
          {selectedPaperTitle && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSelectedPaperTitle("")}
            >
              Clear
            </Button>
          )}
        </HStack>
      </Flex>

      <VStack spacing={5} align="stretch">
        {filteredAttempts.map((att: any) => {
          const allResults = att.questionResult ?? [];


          // Comprehensive counts across all questions
          const totalCount = allResults.length;
          const attemptedCount = allResults.filter((res: any) => res.attempted).length;
          const correctCount = allResults.filter((res: any) => {
            const c = getFinalCorrectness(res.question, res);
            return res.attempted && c === true;
          }).length;
          const wrongCount = allResults.filter((res: any) => {
            const c = getFinalCorrectness(res.question, res);
            return res.attempted && c === false;
          }).length;
          const pendingCount = allResults.filter((res: any) => {
            const c = getFinalCorrectness(res.question, res);
            return res.attempted && c === null;
          }).length;
          const notAttemptedCount = totalCount - attemptedCount;


          // Possible-based totals (all questions), capped
          const { paperTotal, awarded } = computeTotalsFromSections(att);
          const percent = paperTotal > 0 ? Math.round((awarded / paperTotal) * 100) : 0;


          const statusColor = att.isResultPending ? "yellow" : "green";
          const statusText = att.isResultPending ? "Pending" : "Reviewed";
          const attemptAdminComment = att.adminComment || att.adminComments || "";


          return (
            <Box key={att._id} bg={bg} borderColor={border} borderWidth={1} borderRadius="md" shadow="sm" p={5}>
              <Flex justify="space-between" align="center" mb={2}>
                <Box px={3} py={2} borderRadius="md">
                  <Heading size="sm" noOfLines={1}>{att.questionPaper?.title || "Untitled"}</Heading>
                  <Text fontSize="sm" noOfLines={1} color="gray.500">
                    Subject: {att.questionPaper?.subject || "N/A"}
                  </Text>
                </Box>
                <HStack spacing={2}>
                  <Badge colorScheme="purple" variant="subtle" px={2} py={1} borderRadius="md">
                    Marks: {awarded}/{paperTotal}
                  </Badge>
                  <Badge colorScheme={percent >= 50 ? "green" : "red"} variant="subtle" px={2} py={1} borderRadius="md">
                    {percent}%
                  </Badge>
                  <Badge colorScheme={statusColor} variant="solid" px={2} py={1} borderRadius="md">
                    {statusText}
                  </Badge>
                </HStack>
              </Flex>


              {attemptAdminComment && (
                <Box mb={3} p={2} borderWidth={1} borderColor={border} borderRadius="md" bg={useColorModeValue("blue.50", "blue.900")}>
                  <Text fontWeight="semibold" mb={1}>Admin Comment</Text>
                  <Text noOfLines={2} fontSize="sm">{attemptAdminComment}</Text>
                </Box>
              )}


              <Flex wrap="wrap" justify="space-between" gap={3}>
                <StatBox label="Total Questions" value={totalCount} />
                <StatBox label="Attempted" value={attemptedCount} />
                <StatBox label="Correct" value={correctCount} color="green.600" />
                <StatBox label="Wrong" value={wrongCount} color="red.600" />
                <StatBox label="Pending" value={pendingCount} />
                <StatBox label="Not Attempted" value={notAttemptedCount} />
              </Flex>


              <Flex mt={3} justify="flex-end">
                <Button size="sm" colorScheme="blue" onClick={() => openReview(att)}>View Detailed Review</Button>
              </Flex>
            </Box>
          );
        })}
      </VStack>

      {/* Show message when no attempts match filter */}
      {selectedPaperTitle && filteredAttempts.length === 0 && (
        <Box textAlign="center" py={10}>
          <Text color="gray.500">No attempts found for "{selectedPaperTitle}"</Text>
        </Box>
      )}


      {/* Review Modal */}
      {selectedAttempt && (
        <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside" motionPreset="slideInBottom">
          <ModalOverlay />
          <ModalContent maxH="85vh" overflowY="auto">
            <ModalHeader fontSize="md" fontWeight="bold" textAlign="center">
              {selectedAttempt.questionPaper?.title || "Attempt Review"}
            </ModalHeader>
            <ModalCloseButton />
            <Box px={6} pb={6} overflowY="auto" maxH="75vh">
              {/* Paper header */}
              {(() => {
                const qp = selectedAttempt.questionPaper || {};
                const { paperTotal, awarded } = computeTotalsFromSections(selectedAttempt);
                const percent = paperTotal > 0 ? Math.round((awarded / paperTotal) * 100) : 0;


                return (
                  <Box mb={4} p={3} borderWidth={1} borderRadius="md" bg={useColorModeValue("gray.50", "gray.800")}>
                    <SimpleGrid columns={[2, 4]} spacing={3}>
                      <Box>
                        <Text fontSize="xs" color="gray.600">Subject</Text>
                        <Text fontWeight="semibold" fontSize="sm">{qp.subject || "N/A"}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.600">Duration</Text>
                        <Text fontWeight="semibold" fontSize="sm">{qp.duration ?? "N/A"} mins</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.600">Total Marks</Text>
                        <Text fontWeight="semibold" fontSize="sm">{paperTotal}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.600">Score</Text>
                        <Text fontWeight="semibold" fontSize="sm">{awarded}/{paperTotal} ({percent}%)</Text>
                      </Box>
                    </SimpleGrid>
                  </Box>
                );
              })()}


              {/* Sections */}
              {(() => {
                const qp = selectedAttempt.questionPaper || {};
                const sections = Array.isArray(qp.sections) ? qp.sections : [];
                const { sectionMap } = computeTotalsFromSections(selectedAttempt);


                // Group results by section index
                const grouped: Record<number, any[]> = {};
                (selectedAttempt.questionResult || []).forEach((item: any) => {
                  const qid = getSnapshotQuestionId(item.question);
                  let ownerIndex = -1;
                  sections.forEach((sec: any, si: number) => {
                    if (ownerIndex !== -1) return;
                    if (Array.isArray(sec.questions)) {
                      if (sec.questions.some((x: any) => String(x.questionId) === String(qid))) {
                        ownerIndex = si;
                      }
                    }
                  });
                  if (ownerIndex === -1) ownerIndex = 0;
                  if (!grouped[ownerIndex]) grouped[ownerIndex] = [];
                  grouped[ownerIndex].push(item);
                });


                return (
                  <VStack spacing={4} align="stretch">
                    {sections.map((sec: any, si: number) => {
                      // Descending by update time
                      const items = (grouped[si] || []).slice().sort((a: any, b: any) => {
                        const at = new Date(a.updatedAt || a.createdAt || 0).getTime();
                        const bt = new Date(b.updatedAt || b.createdAt || 0).getTime();
                        if (bt !== at) return bt - at;
                        return String(getSnapshotQuestionId(b.question) || b._id || "")
                          .localeCompare(String(getSnapshotQuestionId(a.question) || a._id || ""));
                      });


                      return (
                        <Box key={si} p={3} borderWidth={1} borderRadius="md" bg={useColorModeValue("white", "gray.700")}>
                          <Flex justify="space-between" align="center" mb={2} wrap="wrap" gap={2}>
                            <Box>
                              <Heading size="sm">{sec.name || `Section ${si + 1}`}</Heading>
                            </Box>
                          </Flex>


                          <VStack spacing={3} align="stretch">
                            {items.map((item: any, idx: number) => {
                              const q = item.question;
                              const type = (q?.type || "").toLowerCase();


                              const ua = formatUserAnswer(q, item);
                              const isObjective = isCheckableType(type);
                              const ca = isObjective ? formatCorrectAnswer(q) : "Manual Review";


                              const finalCorrect = getFinalCorrectness(q, item);
                              const possibleMarks = getPossibleMarksFromSections(item, sectionMap);
                              const awardedMarks = computeAwardedFromSections(item, sectionMap);


                              const perQuestionAdminComment = item.adminComment || item.comment || "";
                              const hasImage = !!buildImageUrl(q?.questionImg);
                              const notAttempted = !item.attempted;


                              // Read submitted uploads (image/PDF URLs) saved on server
                              const uploads: string[] = Array.isArray(item.uploads) ? item.uploads : [];


                              return (
                                <Box
                                  key={`${si}-item-${idx}`}
                                  p={3}
                                  rounded="md"
                                  borderWidth={1}
                                  bg={
                                    notAttempted
                                      ? useColorModeValue("gray.50", "gray.800")
                                      : finalCorrect === true
                                      ? "green.50"
                                      : finalCorrect === false
                                      ? "red.50"
                                      : "gray.50"
                                  }
                                  borderColor={
                                    notAttempted
                                      ? "gray.300"
                                      : finalCorrect === true
                                      ? "green.300"
                                      : finalCorrect === false
                                      ? "red.300"
                                      : "gray.300"
                                  }
                                  opacity={notAttempted ? 0.7 : 1}
                                >
                                  <Flex justify="space-between" align="start" gap={3}>
                                    <Box flex="1">
                                      <Text fontWeight="medium" mb={1} fontSize="sm">
                                        {q?.question || "Question"}
                                      </Text>
                                      <Text fontSize="xs" color="gray.600" mb={2}>
                                        Type: {q?.type?.toUpperCase() || "N/A"}
                                        <Badge ml={2} colorScheme="purple" variant="subtle">
                                          Question Marks: {possibleMarks}
                                        </Badge>
                                        {notAttempted && (
                                          <Badge ml={2} colorScheme="gray" variant="subtle">Not attempted</Badge>
                                        )}
                                      </Text>
                                    </Box>
                                    <Box textAlign="right" minW="120px">
                                      {typeof finalCorrect === "boolean" ? (
                                        <Badge colorScheme={finalCorrect ? "green" : "red"} variant="subtle" px={2} py={1}>
                                          {finalCorrect ? "Correct" : "Incorrect"}
                                        </Badge>
                                      ) : (
                                        <Badge colorScheme="yellow" variant="subtle" px={2} py={1}>Pending</Badge>
                                      )}
                                      <Text mt={1} fontWeight="semibold" fontSize="sm">
                                        {awardedMarks}/{possibleMarks}
                                      </Text>
                                    </Box>
                                  </Flex>


                                  {hasImage && <DiagramInline question={q} />}


                                  <Divider my={3} />


                                  {/* Answer Section: Show text answer and/or uploaded files */}
                                  <Box>
                                    <Heading size="xs" mb={2} color="gray.700">
                                      Your Answer
                                    </Heading>
                                    
                                    {/* Text Answer - always show if question type supports it */}
                                    {["short", "long", "la", "diagram"].includes(type) && (
                                      <Box mb={uploads.length > 0 ? 3 : 0} p={2} borderWidth="1px" borderRadius="md" bg={useColorModeValue("gray.50", "gray.800")}>
                                        <Text fontSize="sm" whiteSpace="pre-wrap">
                                          {formatAnswer(ua) === "—" ? (
                                            <Text as="em" color="gray.500">No text answer provided</Text>
                                          ) : (
                                            formatAnswer(ua)
                                          )}
                                        </Text>
                                      </Box>
                                    )}
                                    
                                    {/* Uploaded Files */}
                                    {uploads.length > 0 && (
                                      <Box>
                                        <SubmittedUploads uploads={uploads} />
                                      </Box>
                                    )}
                                    
                                    {/* Neither text nor uploads */}
                                    {formatAnswer(ua) === "—" && uploads.length === 0 && (
                                      <Text fontSize="sm" color="gray.500" as="em">
                                        No answer provided
                                      </Text>
                                    )}
                                  </Box>


                                  <Text fontSize="sm" mt={2}>
                                    <strong>Correct Answer:</strong> {ca}
                                  </Text>


                                  {isObjective && <OptionsBlock item={item} />}


                                  {perQuestionAdminComment && (
                                    <Box mt={3} p={2} bg="blue.50" borderWidth={1} borderRadius="md">
                                      <Text fontWeight="semibold" color="blue.800" mb={1} fontSize="sm">
                                        Comment
                                      </Text>
                                      <Text whiteSpace="pre-wrap" fontSize="sm">{perQuestionAdminComment}</Text>
                                    </Box>
                                  )}
                                </Box>
                              );
                            })}
                          </VStack>
                        </Box>
                      );
                    })}
                  </VStack>
                );
              })()}
            </Box>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
}
